import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PipelineProjectService, {
  type ChangeOrder,
  type CreateForceAccountBody,
  type CreateForceAccountLine,
  type ForceAccountTicket,
  type TicketLine,
} from '@frontend/AppSettings/services/PipelineProjectService';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import { useCatalogOptions } from '@frontend/BidSheets/Admin/useCatalogOptions';
import { money, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
import MsaRatesPanel from './MsaRatesPanel';
import CoHistory from './CoHistory';
import NoticeOfChange from './NoticeOfChange';
import { buildFaBillingRows, buildFaDailyReportRows, FA_REASONS } from '../faExports';
import { crewStatus } from '../crewStatus';

/**
 * The Commercial tab (Part II P4b): change orders, force account and crew loading as ONE
 * workspace — the demo's consolidation, kept as a product decision — plus the bid inventory.
 *
 * The money boundaries mirror the server:
 *  - CHANGE ORDERS are a money document end to end: the server answers 403 for a blind role, and
 *    this tab renders that as an explicit restriction, never an empty list.
 *  - FORCE ACCOUNT splits: any member of the matter may RECORD a day (hours + a reason; the
 *    server prices and decides billability) and read the days back — dollars stripped; the
 *    billable/absorbed totals render only for financial roles.
 *  - CREW LOADING and INVENTORY are money-free by construction; the Over/Under/On-Plan status is
 *    derived client-side exactly as the design source derives it.
 */
interface CommercialTabProps {
  matterid: string;
  /** The source bid's UUID (matters.bid_id) — feeds the frozen MSA rates panel. */
  bidId?: string | null;
}

const CO_STATUSES = [
  'Potential', 'Notice required', 'Drafted', 'Submitted',
  'Approved', 'Rejected', 'Disputed', 'Closed',
];

const CO_VARIANT: Record<string, 'active' | 'pending' | 'completed' | 'at-risk' | 'open'> = {
  Potential: 'open', 'Notice required': 'at-risk', Drafted: 'pending', Submitted: 'active',
  Approved: 'completed', Rejected: 'at-risk', Disputed: 'at-risk', Closed: 'completed',
};

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** The server's replace guard, mirrored: Closed is settled history, and approval freezes a
 * baseline (baseline_version != null) that refuses line rewrites INDEPENDENT of status — an
 * Approved CO is already locked. The reason feeds both `disabled` and `title`: a greyed control
 * with no reason reads as broken (the Long Sheet export-button precedent). */
const lineLockReason = (co: ChangeOrder): string | undefined =>
  co.status === 'Closed'
    ? 'Closed — a settled change order refuses every edit'
    : co.baseline_version != null
      ? `Approval froze baseline v${co.baseline_version} — lines can no longer be rewritten`
      : undefined;

/** The design source's derivation, verbatim: actual vs planned headcount. Moved to its own
 * module for the timesheet tab's overload advisory (validation set, decision f) — re-exported
 * here so this tab's surface is unchanged; ONE copy either way. */
export { crewStatus };

const CREW_VARIANT: Record<string, 'active' | 'completed' | 'at-risk'> = {
  Over: 'at-risk', Under: 'active', 'On Plan': 'completed',
};

/** One FA draft line (fa-ticket-edit): the timesheet's DraftLine shape, minus the fields the
 * FA resource deliberately has no home for (cost codes, payroll split, unit links).
 * `description` carries the STORED name in edit mode so a save before the catalog options
 * resolve does not degrade it to '' (the timesheet lesson). */
const EMPTY_FA_LINE = {
  kind: 'labour' as 'labour' | 'equipment',
  catalogId: '', description: '', qty: '1', hours: '',
};
/** P7b (blocker B9): the draft carries the stored line's IDENTITY and its release state.
 *
 * `id` is intersected in rather than added to `EMPTY_FA_LINE`, because an empty string must NOT
 * read as an identity — a new line has no id at all, and `id: ''` would send one. `protection`
 * is the reason a line is not the author's to change; it is derived from the read rather than
 * stored, and a protected line renders READ-ONLY. Without that the form ships an editable field
 * whose save 400s, which teaches people the server is broken. */
type FaDraftLine = typeof EMPTY_FA_LINE & {
  id?: string;
  protection?: string;
  /** WHOSE hours these are (0049). It is round-tripped rather than displayed, and dropping it is
   *  not cosmetic in either direction: on a SWEPT line the server compares it to decide whether a
   *  protected row is being edited, so a payload without it never compares equal and every save of
   *  a mixed ticket 400s; on an AUTHOR-OWNED line the upsert would write the omission through and
   *  ERASE the person — the defect the read-side fix was supposed to close. There is no roster
   *  fan-out on this surface (accept-only, worklist W1), so the form never sets it; it only has to
   *  carry it. */
  employeeId?: string;
};

/** WHY this stored line is not the author's to re-price, or undefined if it is.
 *
 * All three tests, because none of them is sufficient alone. The source pointer is
 * `ON DELETE SET NULL`, so a swept row whose timesheet line was later deleted has a NULL pointer;
 * `release_status` catches held rows but not swept-AND-released ones; and `released_by` never
 * fires on an author-written row. The server keeps the same three. */
const faProtection = (line: TicketLine): string | undefined => {
  if (line.source_timesheet_line_id) return 'swept from an approved timesheet';
  if (line.released_by) return 'released by a project manager';
  if (line.release_status && line.release_status !== 'released') {
    return `${line.release_status} — awaiting a project manager`;
  }
  return undefined;
};

const EMPTY_FA_FORM = { fa_date: '', reason_code: 'weather', description: '', notes: '' };

const CommercialTab = ({ matterid, bidId }: CommercialTabProps): JSX.Element => {
  const financialVisible = useFinancialVisible();
  const capabilities = useBidCapabilities();
  // Wire values, not the constants' names: roles.py sends lowercase.
  const mayRecord = capabilities?.capabilities.includes('record_production') ?? false;
  // CO writes take SEE_FINANCIALS + MUTATE_BIDS on the server; the executive holds only the
  // first, so without this gate they see a form whose every submit 403s — the exact failure the
  // timesheet tab's gate names. Cosmetic over the server's check, like every gate here.
  const mayAuthorCos = capabilities?.capabilities.includes('mutate_bids') ?? false;
  const queryClient = useQueryClient();

  const changeOrders = useQuery({
    queryKey: ['ChangeOrders', matterid],
    queryFn: () => PipelineProjectService.getChangeOrders(matterid),
  });
  const faTickets = useQuery({
    queryKey: ['ForceAccount', matterid],
    queryFn: () => PipelineProjectService.getForceAccount(matterid),
  });
  const faSummary = useQuery({
    queryKey: ['ForceAccountSummary', matterid],
    queryFn: () => PipelineProjectService.getForceAccountSummary(matterid),
  });
  const pnl = useQuery({
    queryKey: ['Pnl', matterid],
    queryFn: () => PipelineProjectService.getPnl(matterid),
  });
  // Narrowed ONCE: `permitted` discriminates the union, but that narrowing does not survive
  // into the render callbacks below (each closure re-reads the mutable query result, and `!`
  // only strips null — not the Denied member).
  const pnlData = pnl.data?.permitted ? pnl.data : null;
  // Attribution options for change-order lines (P4). /cost-codes is progress structure, not money
  // (project_controls_endpoints' own note), so this loads for anyone who can see the tab — the
  // picker must not be the thing that makes a money-blind user's form fail.
  const costCodes = useQuery({
    queryKey: ['ProjectCostCodes', matterid],
    queryFn: () => PipelineProjectService.getCostCodes(matterid),
  });
  const crew = useQuery({
    queryKey: ['CrewLoading', matterid],
    queryFn: () => PipelineProjectService.getCrewLoading(matterid),
  });
  const inventory = useQuery({
    queryKey: ['ProjectInventory', matterid],
    queryFn: () => PipelineProjectService.getInventory(matterid),
  });
  // The PM's lock list, SHARED with the timesheet tab (same key, one cache): the FA Edit
  // affordance renders only while the day is unverified — cosmetic over the server's own
  // both-dates refusal, like every gate here (fa-ticket-edit decision h).
  const verifiedDays = useQuery({
    queryKey: ['VerifiedDays', matterid],
    queryFn: () => PipelineProjectService.getVerifiedDays(matterid),
  });
  const verifiedSet = useMemo(
    () => new Set((verifiedDays.data ?? []).map((d) => d.day)), [verifiedDays.data]);
  const labourOptions = useCatalogOptions('labour');
  const equipmentOptions = useCatalogOptions('equipment');

  // The Long Sheet's export shape exactly (FieldTimesheetTab): pure rows → one aoa sheet.
  // Gated where the buttons render: money leaves the app here, and no server-side stripping
  // can follow an export — for a blind role the control does not exist.
  // The serializer is ExcelJS behind a lazy import (plans/completed/timesheet-field-ticket-quality/frontend-xlsx-removal.md): the
  // click-time rows are the SAME faExports builders' output — only the writer changed
  // library, and the tab's initial load carries no spreadsheet engine. Sync signature kept
  // so the buttons' onClick props survive byte-for-byte (the fa-ticket-edit rebase note);
  // a failed chunk load must SAY so (the Long Sheet review lesson) — faError is the FA
  // panel's existing slot.
  const exportFaSheet = (rows: (string | number)[][], sheet: string, stem: string) => {
    void (async () => {
      const { downloadAoaWorkbook } = await import('@frontend/Components/Export/aoaWorkbook');
      await downloadAoaWorkbook(
        [{ name: sheet, rows }],
        `${stem}_${matterid}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    })().catch((err) => {
      // The faError slot renders only inside the mayRecord form (a recorded limitation:
      // the byte-for-byte button/UI constraint blocked a new visible slot), so the console
      // line keeps the failure from being fully silent for a financial-only caller.
      console.error('FA export failed:', err);
      setFaError('Could not export the sheet. Nothing was downloaded — try again.');
    });
  };

  // ── Change-order form state ──
  const [coTitle, setCoTitle] = useState('');
  const [coLines, setCoLines] = useState([{ description: '', amount: '', costCodeId: '' }]);
  const [coError, setCoError] = useState<string | null>(null);

  const createCo = useMutation({
    mutationFn: () => PipelineProjectService.createChangeOrder(matterid, {
      title: coTitle,
      lines: coLines
        .filter((l) => l.description)
        // '' means "not attributed" and must go as null, not as an empty string: the backend
        // refuses a non-uuid, and UNCODED is the honest home for a line nobody could code.
        .map((l) => ({
          description: l.description,
          amount: num(l.amount) ?? 0,
          project_cost_code_id: l.costCodeId || null,
        })),
    }),
    onSuccess: () => {
      setCoTitle(''); setCoLines([{ description: '', amount: '', costCodeId: '' }]);
      setCoError(null);
      void queryClient.invalidateQueries({ queryKey: ['ChangeOrders', matterid] });
    },
    onError: (err: any) => setCoError(err?.message ?? 'Could not create the change order.'),
  });

  const transition = useMutation({
    mutationFn: ({ coId, status }: { coId: string; status: string }) =>
      PipelineProjectService.transitionChangeOrder(matterid, coId, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ChangeOrders', matterid] });
      // Prefix key: every expanded trail on this project refreshes — the row the user just
      // wrote is exactly the one they are looking at (review).
      void queryClient.invalidateQueries({ queryKey: ['ChangeOrderHistory', matterid] });
    },
    // A refused move (Closed raced shut, or a capability 403) must SAY so — a select that
    // silently resets teaches the user the move happened.
    onError: (err: any) => setCoError(err?.message ?? 'Could not move the change order.'),
  });

  const toggleLine = useMutation({
    // Replace semantics, honestly: lines carry no ids — the PUT deletes and re-inserts the
    // whole set, re-minting sequence from array order — so a toggle sends the FULL set, built
    // from the server's rendered lines with ONE `included` flipped, never from local draft
    // state. The card total stays the refetched server `requested_value` (the included-sum
    // rule in the subtitle above): no optimistic arithmetic here.
    mutationFn: ({ co, index }: { co: ChangeOrder; index: number }) =>
      PipelineProjectService.replaceChangeOrder(matterid, co.id, {
        lines: co.lines.map((l, idx) => ({
          description: l.description,
          amount: num(l.amount) ?? 0,
          included: idx === index ? !l.included : l.included,
          // Attribution MUST ride along. This is a whole-set replace, so a field omitted here is
          // a field deleted from every line on the change order — toggling one line's `included`
          // would silently strip every cost code the CO was attributed to.
          project_cost_code_id: l.project_cost_code_id ?? null,
          project_phase_id: l.project_phase_id ?? null,
        })),
      }),
    // RETURNED, not void (unlike transitions): the mutation stays pending until both refetches
    // land, so the toggles re-enable only against FRESH lines — a flip during the refetch
    // window would be built from the snapshot this save just replaced, and quietly undo it.
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ChangeOrders', matterid] }),
      // Prefix key, as for transitions: the replace audits `lines: {new: N}` and CoHistory
      // renders it — the row the user just wrote refreshes without a manual reload.
      queryClient.invalidateQueries({ queryKey: ['ChangeOrderHistory', matterid] }),
    ]),
    onError: (err: any) => setCoError(err?.message ?? 'Could not update the impact line.'),
  });

  // ── FA form state (fa-ticket-edit: one form, two modes — multi-line + notes FIRST,
  // because a WHOLESALE replace destroys whatever the form cannot carry: the old
  // single-line, notes-less form would have silently dropped line 2+ and a recorded note
  // on every save, the named A3 failure) ──
  const [faForm, setFaForm] = useState({ ...EMPTY_FA_FORM });
  const [faLines, setFaLines] = useState<FaDraftLine[]>([{ ...EMPTY_FA_LINE }]);
  // null = create; a ticket id = the day being re-authored — submit becomes the PUT, the
  // server re-runs every creation wall (re-pricing from the CURRENT MSA card, re-freezing
  // billable from the reason), and its 400s surface verbatim below.
  const [editingFaId, setEditingFaId] = useState<string | null>(null);
  const [faError, setFaError] = useState<string | null>(null);
  const setFaLine = (i: number, patch: Partial<FaDraftLine>) =>
    setFaLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const createFa = useMutation({
    mutationFn: () => {
      // The timesheet's partial-line doctrine: typed hours without a catalog pick are DATA,
      // and dropping them silently on success is the named silent-drop failure. A truly
      // empty row (an accidental Add) still drops as noise.
      // A PROTECTED row is exempt: the sweep may leave `labour_role_id` NULL, and such a row has
      // hours with no catalog pick — the guard would fire on a line the author can neither edit
      // (read-only) nor remove (no per-line control), deadlocking the ticket.
      const partial = faLines.find((l) => !l.protection && !l.catalogId && l.hours !== '');
      if (partial) {
        return Promise.reject(new Error(
          'A line with hours needs a catalog pick — or clear the row to drop it.'));
      }
      const body: CreateForceAccountBody = {
        fa_date: faForm.fa_date,
        reason_code: faForm.reason_code,
        description: faForm.description,
        ...(faForm.notes ? { notes: faForm.notes } : {}),
        lines: faLines
          .filter((l) => l.catalogId || l.protection)
          .map((l): CreateForceAccountLine => ({
            // Round-tripped so the server can tell an EDIT from a NEW LINE. Absent = new, which
            // is what create sends for every line.
            ...(l.id ? { id: l.id } : {}),
            ...(l.employeeId ? { employeeid: l.employeeId } : {}),
            kind: l.kind,
            // The picked option's label, else the STORED description (edit mode): saving an
            // edit before the options query resolves must not degrade the name to ''.
            //
            // A PROTECTED LINE KEEPS ITS STORED DESCRIPTION VERBATIM, and that is not a nicety.
            // The catalog label is the catalog KEY ("Labourer"), while the sweep copies the
            // timesheet line's own description, which the fan-out writes as "Labourer — Ann
            // Boyd". `description` is one of the fields the server compares to decide whether a
            // protected line is being edited, so re-deriving it makes every swept line look
            // changed and the whole ticket permanently unsavable — the same failure the
            // `employeeId` round-trip fixes, one field over, and the row is read-only so nobody
            // can make it match.
            description: l.protection
              ? (l.description || undefined)
              : ((l.kind === 'labour' ? labourOptions : equipmentOptions)
                .find((o) => o.id === l.catalogId)?.label ?? (l.description || undefined)),
            ...(l.kind === 'labour'
              ? { labour_role_id: l.catalogId } : { equipment_item_id: l.catalogId }),
            qty: num(l.qty) ?? 1,
            hours: num(l.hours) ?? 0,
          })),
      };
      // One body, two verbs (the A3 idiom): an edit is a wholesale replace of the same
      // no-money shape.
      return editingFaId
        ? PipelineProjectService.replaceForceAccount(matterid, editingFaId, body)
        : PipelineProjectService.createForceAccount(matterid, body);
    },
    // RETURNED, not void (the toggle precedent above): the mutation stays pending until the
    // refetches land, so the disabled export buttons re-enable only against FRESH rows — a
    // billing sheet is never cut mid-session from rows the cache already knows are stale.
    // The four keys are the enumerated readers of project_force_account_*: the list, the
    // summary + cascade, the P&L's fa_billable column, and the margin block's with_fa.
    // CREATE invalidates the same set — the parity fix: Pnl and BvA went stale after a
    // create before this build.
    onSuccess: () => {
      setFaForm({ ...EMPTY_FA_FORM });
      setFaLines([{ ...EMPTY_FA_LINE }]);
      setEditingFaId(null);   // a successful edit exits edit mode
      setFaError(null);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ForceAccount', matterid] }),
        queryClient.invalidateQueries({ queryKey: ['ForceAccountSummary', matterid] }),
        queryClient.invalidateQueries({ queryKey: ['Pnl', matterid] }),
        queryClient.invalidateQueries({ queryKey: ['BidVsActual', matterid] }),
      ]);
    },
    onError: (err: any) => setFaError(err?.message ?? 'Could not record the FA day.'),
  });

  /** Load an UNVERIFIED FA day into the form (fa-ticket-edit): the pickers repopulate from
   * the identity ids the list now carries — the load-bearing read fix. The round-trip is
   * FULL for a blind author by construction: every FA form fact — kind, ids, qty, hours,
   * reason, description, notes — survives stripping (unlike field tickets' invoice_value
   * retype wall, no FA form fact is money). */
  const startFaEdit = (t: ForceAccountTicket) => {
    setEditingFaId(t.id);
    setFaForm({
      fa_date: t.fa_date,
      reason_code: t.reason_code,
      description: t.description,
      notes: t.notes ?? '',
    });
    setFaLines(t.lines.length ? t.lines.map((l): FaDraftLine => ({
      ...(l.id ? { id: l.id } : {}),
      ...(l.employeeid ? { employeeId: l.employeeid } : {}),
      protection: faProtection(l),
      kind: l.kind,
      catalogId: (l.kind === 'labour' ? l.labour_role_id : l.equipment_item_id) ?? '',
      description: l.description ?? '',
      qty: l.qty,
      hours: l.hours,
    })) : [{ ...EMPTY_FA_LINE }]);
    setFaError(null);
  };

  const cancelFaEdit = () => {
    setEditingFaId(null);
    setFaForm({ ...EMPTY_FA_FORM });
    setFaLines([{ ...EMPTY_FA_LINE }]);
    setFaError(null);
  };

  return (
    <div className="pipeline-commercial">
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Change orders</h2>
        <p className="pipeline-panel-subtitle">
          Quantified impact lines; the requested value is the server's sum of included lines.
        </p>
        {changeOrders.isLoading && <p className="pipeline-muted">Loading change orders…</p>}
        {changeOrders.data?.permitted === false && (
          <p className="pipeline-muted">
            Change orders are restricted to financial roles on this project.
          </p>
        )}
        {changeOrders.data?.permitted && (
          <>
            {changeOrders.data.change_orders.length === 0 && (
              <p className="pipeline-muted">No change orders yet.</p>
            )}
            {changeOrders.data.change_orders.map((co) => (
              <div className="pipeline-co-card" key={co.id}>
                <div className="pipeline-co-head">
                  <strong>{co.title}</strong>
                  <StatusBadge status={co.status} variant={CO_VARIANT[co.status] ?? 'open'} size="small" />
                  <span className="pipeline-co-value">{money(num(co.requested_value))}</span>
                </div>
                <ul className="pipeline-co-lines">
                  {co.lines.map((line, i) => (
                    <li key={i} className={line.included ? '' : 'pipeline-co-excluded'}>
                      {/* Goes DEAD exactly where the server refuses the PUT: replace_change_order
                          refuses Closed AND any CO that froze a baseline (lineLockReason above) —
                          NOT the transition select's Closed-only gate. Greyed rather than removed
                          so the checked state still shows what the settled CO includes. Also
                          disabled while THIS CO's save is in flight: a second flip would race
                          the whole-set replace and drop the first. */}
                      {mayAuthorCos && (
                        <input
                          type="checkbox"
                          className="pipeline-co-line-toggle"
                          checked={line.included}
                          disabled={lineLockReason(co) !== undefined
                            || (toggleLine.isPending && toggleLine.variables?.co.id === co.id)}
                          title={lineLockReason(co)}
                          aria-label={`Include ${line.description}`}
                          onChange={() => toggleLine.mutate({ co, index: i })}
                        />
                      )}
                      {line.description} · {money(num(line.amount))}
                      {!line.included && ' (withdrawn)'}
                    </li>
                  ))}
                </ul>
                <CoHistory matterid={matterid} coId={co.id} />
                <NoticeOfChange matterid={matterid} co={co} />
                {co.status !== 'Closed' && mayAuthorCos && (
                  <label className="pipeline-co-transition">
                    Move to
                    <select
                      value=""
                      onChange={(e) => e.target.value
                        && transition.mutate({ coId: co.id, status: e.target.value })}
                    >
                      <option value="">…</option>
                      {CO_STATUSES.filter((s) => s !== co.status).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            ))}
            {coError && <p className="pipeline-form-error">{coError}</p>}
            {mayAuthorCos && (
            <form
              className="pipeline-form"
              onSubmit={(e) => { e.preventDefault(); createCo.mutate(); }}
            >
              <div className="pipeline-form-row">
                <label>
                  New change order
                  <input
                    required placeholder="Title" value={coTitle}
                    onChange={(e) => setCoTitle(e.target.value)}
                  />
                </label>
              </div>
              {coLines.map((line, i) => (
                <div className="pipeline-form-row" key={i}>
                  <label>
                    Impact
                    <input
                      placeholder="e.g. Lost stringing production" value={line.description}
                      onChange={(e) => setCoLines((prev) => prev.map(
                        (l, idx) => (idx === i ? { ...l, description: e.target.value } : l)))}
                    />
                  </label>
                  <label>
                    Amount
                    <input
                      /* No min: a DEDUCTIVE line (a credit, or one reversing an earlier
                         approved change order) is ordinary commercial traffic, and it is the only
                         way to take scope back out of a baseline that is additive by construction.
                         Quantities below keep min="0" — a negative hour is not a thing. */
                      /* step="any": the HTML default of 1 makes any amount with cents fail
                         native validation and silently block the form's submit. */
                      type="number" step="any" value={line.amount}
                      onChange={(e) => setCoLines((prev) => prev.map(
                        (l, idx) => (idx === i ? { ...l, amount: e.target.value } : l)))}
                    />
                  </label>
                  <label>
                    Cost code
                    <select
                      value={line.costCodeId}
                      onChange={(e) => setCoLines((prev) => prev.map(
                        (l, idx) => (idx === i ? { ...l, costCodeId: e.target.value } : l)))}
                    >
                      {/* Unattributed is a legitimate answer, offered first and by default: a
                          "general disruption" line has no honest code, and the variance reports it
                          as uncoded rather than filing it under a code nobody chose. */}
                      <option value="">Not attributed</option>
                      {(costCodes.data ?? []).map((cc) => (
                        <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
              ))}
              <div className="pipeline-form-actions">
                <button
                  type="button" className="pipeline-secondary-btn"
                  onClick={() => setCoLines((prev) => [
                    ...prev, { description: '', amount: '', costCodeId: '' }])}
                >
                  Add impact line
                </button>
                <button type="submit" className="pipeline-action-btn" disabled={createCo.isPending}>
                  Open change order
                </button>
              </div>
            </form>
            )}
          </>
        )}
      </section>

      <section className="pipeline-panel">
        <div className="pipeline-panel-header">
          <h2 className="pipeline-panel-title">Force account</h2>
          {financialVisible && !!faTickets.data?.length && (
            <div className="pipeline-fa-exports">
              {/* Disabled while the list refetches (fa-ticket-edit decision i): the exports
                  build from the CLICK-TIME cache, and an edit's invalidation is the moment
                  the cache admits its rows are stale — a billing sheet must not be cut from
                  them until the refetch lands (the returned-Promise.all mutation holds the
                  pending state across exactly this window). */}
              <button
                className="pipeline-secondary-btn"
                disabled={faTickets.isFetching}
                onClick={() => exportFaSheet(
                  buildFaDailyReportRows(matterid, faTickets.data ?? []),
                  'FA Daily Report', 'FA_DailyReport')}
              >
                Export FA Daily Report
              </button>
              <button
                className="pipeline-secondary-btn"
                disabled={faTickets.isFetching}
                onClick={() => exportFaSheet(
                  buildFaBillingRows(matterid, faTickets.data ?? []),
                  'FA Billing', 'FA_Billing')}
              >
                Export client billing sheet
              </button>
            </div>
          )}
        </div>
        <p className="pipeline-panel-subtitle">
          MSA-priced out-of-scope days. The reason decides who pays; the server decides and freezes.
        </p>
        {financialVisible && faSummary.data?.permitted && (
          <div className="pipeline-fa-totals">
            <span>Total {money(num(faSummary.data.totals.total))}</span>
            <span>Billable {money(num(faSummary.data.totals.billable))}</span>
            <span>Absorbed {money(num(faSummary.data.totals.absorbed))}</span>
          </div>
        )}
        {financialVisible && faSummary.data?.permitted
          && (faSummary.data.cascade?.phases.some((p) => p.days_pushed > 0) ?? false) && (
          <>
            {/* The demo's "what the weather slip pushes" table (gap 2.7): recorded FA days on a
                phase push everything downstream, and a pushed phase burns its planned daily
                spend while it waits. Rendered only when there ARE delay days — a cascade of
                zeroes reads as noise. */}
            {/* Gated on something actually PUSHED (not merely recorded): FA days only on
                the last phase push nothing, and that table was all dashes and $0 — the exact
                zero-noise this block's own comment forbids (review). */}
            <h3 className="pipeline-panel-subheading">
              Knock-on cascade — {faSummary.data.cascade!.totals.recorded_fa_days} recorded FA day
              {faSummary.data.cascade!.totals.recorded_fa_days === 1 ? '' : 's'}
            </h3>
            <table className="pipeline-table">
              <thead>
                <tr>
                  <th>Phase</th><th>FA days</th><th>Days pushed</th>
                  <th>Daily standby</th><th>Standby cost</th>
                </tr>
              </thead>
              <tbody>
                {faSummary.data.cascade!.phases.map((row) => (
                  <tr key={row.phase_id}>
                    <td>{row.phase}</td>
                    <td>{row.fa_days || '—'}</td>
                    <td>{row.days_pushed || '—'}</td>
                    <td>{money(num(row.daily_standby))}</td>
                    <td>{money(num(row.standby_cost))}</td>
                  </tr>
                ))}
                <tr className="pipeline-totals-row">
                  {/* A forward EXPOSURE — not additive with the FA totals above or the schedule
                      tab's projection: when these dollars materialize they land as FA tickets
                      or as actuals already feeding the EAC. */}
                  <td>Total downstream standby (exposure)</td>
                  <td /><td />
                  <td>{money(num(faSummary.data.cascade!.totals.daily_standby_exposure))}/day</td>
                  <td>{money(num(faSummary.data.cascade!.totals.standby_cost))}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
        {faTickets.isLoading && <p className="pipeline-muted">Loading force-account days…</p>}
        {faTickets.isError && <p className="pipeline-muted">Could not load force-account days.</p>}
        {faTickets.data?.length === 0 && <p className="pipeline-muted">No FA days recorded.</p>}
        {!!faTickets.data?.length && (
          <table className="pipeline-table">
            <thead>
              <tr><th>Date</th><th>Reason</th><th>Attribution</th><th>Description</th><th>Lines</th></tr>
            </thead>
            <tbody>
              {faTickets.data.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.fa_date}
                    {verifiedSet.has(t.fa_date) && (
                      <span className="pipeline-verified-badge" title="Verified — day is locked">
                        ✓ Verified
                      </span>
                    )}
                    {/* Edit-until-verified for FA (fa-ticket-edit): the affordance exists
                        ONLY while the day is unverified — the server refuses BOTH dates
                        anyway; this is the cosmetic gate over it, like mayRecord. */}
                    {mayRecord && !verifiedSet.has(t.fa_date) && (
                      <button
                        type="button" className="pipeline-secondary-btn"
                        onClick={() => startFaEdit(t)}
                        disabled={createFa.isPending}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                  <td>{FA_REASONS.find((r) => r.code === t.reason_code)?.label ?? t.reason_code}</td>
                  <td>
                    <StatusBadge
                      status={t.billable ? 'Billable' : 'Absorbed'}
                      variant={t.billable ? 'completed' : 'at-risk'}
                      size="small"
                    />
                  </td>
                  <td>{t.description}</td>
                  <td>
                    {t.lines.map((line, i) => (
                      <div key={i} className="pipeline-line-cell">
                        {line.description || line.kind} · {line.qty} × {line.hours}h
                        {line.line_total !== undefined && (
                          <span className="pipeline-line-money"> · {money(num(line.line_total))}</span>
                        )}
                        {/* The rate-source disclosure (P2 Build M, decision 4's third
                            surface): an FA line can now price from the matter's client
                            organization's MSA card while MsaRatesPanel shows the frozen bid
                            card — so every line names its rung. A FACT that survives
                            stripping (the cost_source doctrine); pre-0040 rows carry null
                            and show nothing rather than a guess. */}
                        {line.rate_source && (
                          <span className="pipeline-muted">
                            {' '}· {line.rate_source === 'org_msa' ? 'org rate' : 'catalog rate'}
                          </span>
                        )}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {mayRecord && (
          <form
            className="pipeline-form"
            onSubmit={(e) => { e.preventDefault(); createFa.mutate(); }}
          >
            <div className="pipeline-form-row">
              <label>
                Date
                <input
                  type="date" required value={faForm.fa_date}
                  onChange={(e) => setFaForm({ ...faForm, fa_date: e.target.value })}
                />
              </label>
              <label>
                Reason
                <select
                  value={faForm.reason_code}
                  onChange={(e) => setFaForm({ ...faForm, reason_code: e.target.value })}
                >
                  {FA_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Description
                <input
                  required placeholder="e.g. Stringing crew standby — wet ROW"
                  value={faForm.description}
                  onChange={(e) => setFaForm({ ...faForm, description: e.target.value })}
                />
              </label>
              <label>
                Notes
                {/* On the wire since P4b but never in the form — surfaced by this build:
                    a WHOLESALE edit that cannot carry notes nulls a recorded one. */}
                <input
                  value={faForm.notes}
                  onChange={(e) => setFaForm({ ...faForm, notes: e.target.value })}
                />
              </label>
            </div>
            {faLines.map((line, i) => (
              <div className="pipeline-form-row pipeline-line-row" key={line.id ?? i}>
                <label>
                  Kind
                  <select
                    value={line.kind}
                    disabled={!!line.protection}
                    onChange={(e) => setFaLine(i, {
                      kind: e.target.value as FaDraftLine['kind'],
                      catalogId: '', description: '' })}
                  >
                    <option value="labour">Labour</option>
                    <option value="equipment">Equipment</option>
                  </select>
                </label>
                <label>
                  {line.kind === 'labour' ? 'Role' : 'Equipment'}
                  <select
                    value={line.catalogId}
                    disabled={!!line.protection}
                    onChange={(e) => setFaLine(i, { catalogId: e.target.value })}
                  >
                    <option value="">Pick from catalog…</option>
                    {(line.kind === 'labour' ? labourOptions : equipmentOptions).map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Qty
                  <input
                    type="number" min="0" value={line.qty}
                    readOnly={!!line.protection}
                    onChange={(e) => setFaLine(i, { qty: e.target.value })}
                  />
                </label>
                <label>
                  Hours
                  <input
                    type="number" min="0" value={line.hours}
                    readOnly={!!line.protection}
                    onChange={(e) => setFaLine(i, { hours: e.target.value })}
                  />
                </label>
                {/* P7b: a line the commercial sweep created, or one a PM has acted on, is NOT the
                    author's to re-price — the server refuses the change and would otherwise do it
                    on save, teaching people the form is broken. The row stays VISIBLE and is sent
                    back unchanged, because the server skips an unchanged protected line rather
                    than refusing it; hiding it would make the payload look like a deletion. */}
                {line.protection && (
                  <span className="pipeline-line-note" title={line.protection}>
                    Locked — {line.protection}
                  </span>
                )}
              </div>
            ))}
            <div className="pipeline-form-actions">
              <button
                type="button" className="pipeline-secondary-btn"
                onClick={() => setFaLines((prev) => [...prev, { ...EMPTY_FA_LINE }])}
              >
                Add line
              </button>
              {editingFaId && (
                <button
                  type="button" className="pipeline-secondary-btn"
                  onClick={cancelFaEdit}
                >
                  Cancel edit
                </button>
              )}
              <button type="submit" className="pipeline-action-btn" disabled={createFa.isPending}>
                {createFa.isPending ? 'Saving…' : editingFaId ? 'Save FA day' : 'Record FA day'}
              </button>
            </div>
            {faError && <p className="pipeline-form-error">{faError}</p>}
          </form>
        )}
      </section>

      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Crew loading</h2>
        <p className="pipeline-panel-subtitle">
          {crew.data?.as_of_date
            ? `Planned crew from the bid vs the day recorded ${crew.data.as_of_date}.`
            : 'Planned crew from the bid; actuals arrive with field tickets.'}
        </p>
        {crew.isLoading && <p className="pipeline-muted">Loading crew loading…</p>}
        {crew.isError && <p className="pipeline-muted">Could not load crew loading.</p>}
        {crew.data && crew.data.crews.length === 0 && (
          <p className="pipeline-muted">No phases on this project.</p>
        )}
        {!!crew.data?.crews.length && (
          <table className="pipeline-table">
            <thead>
              <tr><th>Phase</th><th>Planned</th><th>Actual</th><th>Man hrs</th><th>Status</th></tr>
            </thead>
            <tbody>
              {crew.data.crews.map((row) => {
                const status = crewStatus(
                  num(row.planned_headcount) ?? 0, num(row.actual_headcount) ?? 0);
                return (
                  <tr key={row.phase_id ?? row.phase}>
                    <td>{row.phase}</td>
                    <td>{row.planned_headcount}</td>
                    <td>{row.actual_headcount}</td>
                    <td>{row.man_hours}</td>
                    <td><StatusBadge status={status} variant={CREW_VARIANT[status]} size="small" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!!crew.data?.equipment?.length && (
          <>
            <h3 className="pipeline-panel-subheading">Equipment loading</h3>
            <table className="pipeline-table">
              <thead>
                <tr>
                  <th>Phase</th><th>Planned units</th><th>Actual units</th>
                  <th>Equip hrs</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {crew.data.equipment.map((row) => {
                  const pct = num(row.percent_complete);
                  const units = num(row.actual_units) ?? 0;
                  // The demo's margin-leak alarm, derived client-side like crewStatus: a phase
                  // ≥90% done with iron still on site is money burning against finished work —
                  // "phase nearly done, send it home".
                  const demob = pct !== undefined && pct >= 90 && units > 0;
                  const status = demob
                    ? 'Demob candidate'
                    : crewStatus(num(row.planned_units) ?? 0, units);
                  return (
                    <tr key={row.phase_id ?? row.phase}>
                      <td>
                        {row.phase}
                        {pct !== undefined && (
                          <span className="pipeline-muted"> · {pct.toFixed(0)}% done</span>
                        )}
                      </td>
                      <td>{row.planned_units}</td>
                      <td>{row.actual_units}</td>
                      <td>{row.equipment_hours}</td>
                      <td>
                        <StatusBadge
                          status={status}
                          variant={demob ? 'at-risk' : CREW_VARIANT[status]}
                          size="small"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </section>

      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Inventory</h2>
        <p className="pipeline-panel-subtitle">Equipment and small tools assigned to this job's bid.</p>
        {inventory.isLoading && <p className="pipeline-muted">Loading inventory…</p>}
        {inventory.isError && <p className="pipeline-muted">Could not load inventory.</p>}
        {inventory.data?.length === 0 && (
          <p className="pipeline-muted">No inventory assigned to this job.</p>
        )}
        {!!inventory.data?.length && (
          <table className="pipeline-table">
            <thead>
              <tr><th>Name</th><th>Category</th><th>Asset tag</th><th>Qty</th></tr>
            </thead>
            <tbody>
              {inventory.data.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.category ?? '—'}</td>
                  <td>{item.asset_tag ?? '—'}</td>
                  <td>{item.quantity ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Its own panel, deliberately NOT inside the Daily P&L block below: that block renders only
          once days are recorded, and exposure matters most before any work has happened — a change
          order raised on day one is exactly the number a PM wants and would be invisible there.
          Zero-suppressed like that block, though: the server always emits the object (COALESCE'd
          zeros), and a standing "$0 across 0 change orders" on every quiet project is the
          cascade's zero-noise again. */}
      {financialVisible && pnlData?.change_order_exposure
        && pnlData.change_order_exposure.change_orders > 0 && (
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Net change-order exposure</h2>
        <p className="pipeline-panel-subtitle">
          Requested and not yet agreed, netted: a pending credit or reversal subtracts here, so the
          figure can be negative and that is information, not an error. Not counted in revenue or
          cost — an approved change order becomes scope and is measured as planned, so counting it
          here too would double it the moment it is agreed.
        </p>
        <p className="pipeline-figure">
          {money(num(pnlData.change_order_exposure.value))}
          <span className="pipeline-figure-note">
            {' '}
            across {pnlData.change_order_exposure.change_orders}{' '}
            {pnlData.change_order_exposure.change_orders === 1 ? 'change order' : 'change orders'}
            {' — counting '}
            {pnlData.change_order_exposure.statuses.join(', ')}
          </span>
        </p>
      </section>
      )}

      {financialVisible && pnlData && pnlData.days.length > 0 && (
      <section className="pipeline-panel">
        <h2 className="pipeline-panel-title">Daily P&L</h2>
        <p className="pipeline-panel-subtitle">
          {/* "Work value" is the charge-out build-up, NOT cash billed (the plan's proxy
              hazard: mislabelled as billed, a PM reconciles it against invoices and finds a
              lie). The Overhead column exists only when a rate RESOLVES — this bid's own,
              else the company default (Tier 4.1 rider); neither, and the line stays absent. */}
          Work valued at charge-out vs frozen internal cost, per recorded day.
          {pnlData.totals.cost_estimated !== '0.00' && ' Cost figures include estimates.'}
          {/* The overhead deduction says its rate AND which rung set it (Tier 4.1 rider): a
              figure taken off every day's net must not be unexplained, and "this bid's own"
              vs "the company default" is the difference between a decision and an inheritance. */}
          {/* The rate renders EXACTLY as the server normalised it, not through percent(),
              which rounds: a 12.5% overhead shown as "13%" beside an exact deduction is a
              small lie. Raw inside a financialVisible gate is the MarginPanel precedent. */}
          {pnlData.overhead_configured && pnlData.overhead_on_revenue_pct != null && (
            ` Corporate overhead on revenue at ${pnlData.overhead_on_revenue_pct}%`
            + `${pnlData.overhead_on_revenue_source === 'bid' ? " (this bid's own rate)"
              : pnlData.overhead_on_revenue_source === 'global' ? ' (the company default)' : ''}.`
          )}
        </p>
        <table className="pipeline-table">
          <thead>
            <tr>
              <th>Date</th><th>Work value</th><th>FA billable</th>
              {pnlData.cost_plus_configured && <th>Invoices +%</th>}
              <th>Revenue</th><th>Cost</th><th>Gross</th>
              {pnlData.overhead_configured && <th>Overhead</th>}
              <th>Net</th>
            </tr>
          </thead>
          <tbody>
            {pnlData.days.map((d) => (
              <tr key={d.date}>
                <td>{d.date}</td>
                <td>{money(num(d.work_at_charge_out))}</td>
                <td>{money(num(d.fa_billable))}</td>
                {pnlData.cost_plus_configured && <td>{money(num(d.invoice_cost_plus))}</td>}
                <td>{money(num(d.revenue))}</td>
                <td>{money(num(d.cost))}</td>
                <td>{money(num(d.gross))}</td>
                {pnlData.overhead_configured && (
                  <td>{d.overhead != null ? money(num(d.overhead)) : '—'}</td>
                )}
                <td>
                  {/* NO confident net over uncosted hours — withheld, with the badge saying
                      why (the "Internal cost unrecorded" rule at day grain). */}
                  {d.net != null ? money(num(d.net))
                    : `— (${d.unknown_hours} h uncosted)`}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="pipeline-bold">Total</td>
              <td>{money(num(pnlData.totals.work_at_charge_out))}</td>
              <td>{money(num(pnlData.totals.fa_billable))}</td>
              {pnlData.cost_plus_configured
                && <td>{money(num(pnlData.totals.invoice_cost_plus))}</td>}
              <td>{money(num(pnlData.totals.revenue))}</td>
              <td>{money(num(pnlData.totals.cost))}</td>
              <td>{money(num(pnlData.totals.gross))}</td>
              {pnlData.overhead_configured && (
                <td>{pnlData.totals.overhead != null
                  ? money(num(pnlData.totals.overhead)) : '—'}</td>
              )}
              <td>{pnlData.totals.net != null ? money(num(pnlData.totals.net))
                : `— (${pnlData.totals.unknown_hours} h uncosted)`}</td>
            </tr>
          </tfoot>
        </table>
      </section>
      )}

      <MsaRatesPanel bidId={bidId} />
    </div>
  );
};

export default CommercialTab;
